import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BLOOD_TYPES   = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];
const GENDERS       = ['Male', 'Female', 'Other', 'Prefer not to say'];
const RELATIONSHIPS = ['self', 'spouse', 'parent', 'child', 'sibling', 'other'];
const EMERGENCY_RELATIONS = ['Spouse', 'Parent', 'Child', 'Sibling', 'Friend', 'Other'];

const NAV_ITEMS = [
  { key: 'my-profile', label: 'My Profile', icon: '👤' },
  { key: 'password',   label: 'Password',   icon: '🔒' },
  { key: 'billing',    label: 'Plan & Usage', icon: '💳' },
  { key: 'apis',       label: 'APIs',        icon: '🔌' },
];

const lbl = { display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500', fontSize: '0.9rem' };
const inp = (err) => ({
  width: '100%', padding: '10px 12px', boxSizing: 'border-box', fontSize: '0.95rem',
  border: `1.5px solid ${err ? '#e53e3e' : '#e2e8f0'}`, borderRadius: '8px',
});
const btnPrimary = {
  padding: '10px 28px', background: '#2b6cb0', color: 'white',
  border: 'none', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer',
};

// ── Shared alert ──────────────────────────────────────────────────────────────
const Alert = ({ type, msg }) => msg ? (
  <div style={{
    padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
    background: type === 'error' ? '#fff5f5' : '#f0fff4',
    color:      type === 'error' ? '#c53030' : '#22543d',
    border: `1px solid ${type === 'error' ? '#fed7d7' : '#9ae6b4'}`,
  }}>{msg}</div>
) : null;

// ── My Profile tab — embeds the patient form inline ───────────────────────────
function MyProfileTab({ user }) {
  const token   = useMemo(() => localStorage.getItem('token'), []);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const EMPTY = {
    first_name: '', last_name: '', date_of_birth: '', gender: '',
    blood_type: '', allergies: '', phone: '', email: '', address: '',
    height: '', emergency_contact_name: '', emergency_contact_phone: '',
    emergency_contact_relation: '', relationship_to_admin: 'self',
  };

  const [selfPatientId, setSelfPatientId] = useState(null);
  const [formData, setFormData] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  const loadSelfPatient = useCallback(async () => {
    try {
      const res = await api.get(`${API_URL}/patients`, { headers });
      const self = (res.data.patients || []).find(p => p.relationship_to_admin === 'self');
      if (self) {
        setSelfPatientId(self.patient_id);
        const detail = await api.get(`${API_URL}/patients/${self.patient_id}`, { headers });
        const p = detail.data.patient;
        setFormData({
          first_name: p.first_name || '', last_name: p.last_name || '',
          date_of_birth: p.date_of_birth || '', gender: p.gender || '',
          blood_type: p.blood_type || '', allergies: p.allergies || '',
          phone: p.phone || '', email: p.email || '', address: p.address || '',
          height: p.height || '',
          emergency_contact_name:     p.emergency_contact_name     || '',
          emergency_contact_phone:    p.emergency_contact_phone    || '',
          emergency_contact_relation: p.emergency_contact_relation || '',
          relationship_to_admin:      p.relationship_to_admin      || 'self',
        });
      } else {
        // No self-patient — prefill from user account
        setFormData(prev => ({ ...prev, first_name: user?.first_name || '', last_name: user?.last_name || '', email: user?.email || '' }));
      }
    } catch (_) {
      setError('Failed to load profile data.');
    } finally {
      setLoading(false);
    }
  }, [headers, user]);

  useEffect(() => { loadSelfPatient(); }, [loadSelfPatient]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
    setError(''); setSuccess('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');

    const errs = {};
    if (!formData.first_name.trim()) errs.first_name = 'Required';
    if (!formData.last_name.trim())  errs.last_name  = 'Required';
    if (!formData.date_of_birth)     errs.date_of_birth = 'Required';
    if (!formData.gender)            errs.gender = 'Required';
    if (!formData.phone.trim())      errs.phone = 'Required';
    else if (!/^\+\d{10,15}$/.test(formData.phone.trim())) errs.phone = 'Include country code, e.g. +919876543210';
    if (!formData.email.trim())      errs.email = 'Required';
    if (!formData.emergency_contact_name.trim())  errs.emergency_contact_name = 'Required';
    if (!formData.emergency_contact_phone.trim()) errs.emergency_contact_phone = 'Required';
    else if (!/^\+\d{10,15}$/.test(formData.emergency_contact_phone.trim())) errs.emergency_contact_phone = 'Include country code';

    if (Object.keys(errs).length) {
      setFieldErrors(errs); setError('Please fix the errors below.'); setSaving(false); return;
    }

    const payload = {
      ...formData,
      first_name: formData.first_name.trim(), last_name: formData.last_name.trim(),
      phone: formData.phone.trim(), email: formData.email.trim(),
      address: formData.address.trim(),
      height: formData.height ? parseFloat(formData.height) : null,
      emergency_contact_name:  formData.emergency_contact_name.trim(),
      emergency_contact_phone: formData.emergency_contact_phone.trim(),
      emergency_contact_relation: formData.emergency_contact_relation.trim(),
    };

    try {
      if (selfPatientId) {
        await api.put(`${API_URL}/patients/${selfPatientId}`, payload, { headers });
      } else {
        const res = await api.post(`${API_URL}/patients`, payload, { headers });
        setSelfPatientId(res.data.patient?.patient_id || null);
      }
      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ color: '#718096' }}>Loading profile…</div>;

  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' };

  return (
    <div style={{ maxWidth: '720px' }}>
      <h2 style={{ color: '#2d3748', marginBottom: '24px', fontSize: '1.4rem', fontWeight: '700' }}>My Profile</h2>
      <Alert type="error"   msg={error} />
      <Alert type="success" msg={success} />

      <form onSubmit={handleSubmit}>

        {/* Name */}
        <div style={row2}>
          <div>
            <label style={lbl}>First Name *</label>
            <input name="first_name" value={formData.first_name} onChange={handleChange} style={inp(fieldErrors.first_name)} />
            {fieldErrors.first_name && <span style={{ color: '#e53e3e', fontSize: '0.8rem' }}>{fieldErrors.first_name}</span>}
          </div>
          <div>
            <label style={lbl}>Last Name *</label>
            <input name="last_name" value={formData.last_name} onChange={handleChange} style={inp(fieldErrors.last_name)} />
            {fieldErrors.last_name && <span style={{ color: '#e53e3e', fontSize: '0.8rem' }}>{fieldErrors.last_name}</span>}
          </div>
        </div>

        {/* DOB / Gender / Blood type */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={lbl}>Date of Birth *</label>
            <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} style={inp(fieldErrors.date_of_birth)} />
            {fieldErrors.date_of_birth && <span style={{ color: '#e53e3e', fontSize: '0.8rem' }}>{fieldErrors.date_of_birth}</span>}
          </div>
          <div>
            <label style={lbl}>Gender *</label>
            <select name="gender" value={formData.gender} onChange={handleChange} style={inp(fieldErrors.gender)}>
              <option value="">Select</option>
              {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            {fieldErrors.gender && <span style={{ color: '#e53e3e', fontSize: '0.8rem' }}>{fieldErrors.gender}</span>}
          </div>
          <div>
            <label style={lbl}>Blood Type</label>
            <select name="blood_type" value={formData.blood_type} onChange={handleChange} style={inp()}>
              <option value="">Select</option>
              {BLOOD_TYPES.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>

        {/* Phone / Email */}
        <div style={row2}>
          <div>
            <label style={lbl}>Phone *</label>
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91XXXXXXXXXX" style={inp(fieldErrors.phone)} />
            {fieldErrors.phone && <span style={{ color: '#e53e3e', fontSize: '0.8rem' }}>{fieldErrors.phone}</span>}
          </div>
          <div>
            <label style={lbl}>Email *</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} style={inp(fieldErrors.email)} />
            {fieldErrors.email && <span style={{ color: '#e53e3e', fontSize: '0.8rem' }}>{fieldErrors.email}</span>}
          </div>
        </div>

        {/* Height / Relationship */}
        <div style={row2}>
          <div>
            <label style={lbl}>Height (cm)</label>
            <input type="number" name="height" value={formData.height} onChange={handleChange} placeholder="e.g. 175" step="0.1" style={inp()} />
            <small style={{ color: '#a0aec0', fontSize: '0.78rem' }}>Used for BMI calculation</small>
          </div>
          <div>
            <label style={lbl}>Relationship to account</label>
            <select name="relationship_to_admin" value={formData.relationship_to_admin} onChange={handleChange} style={inp()}>
              <option value="">Select</option>
              {RELATIONSHIPS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>
        </div>

        {/* Allergies */}
        <div style={{ marginBottom: '16px' }}>
          <label style={lbl}>Allergies</label>
          <textarea name="allergies" value={formData.allergies} onChange={handleChange} rows="2"
            placeholder="List any allergies (comma-separated)"
            style={{ ...inp(), resize: 'vertical' }} />
        </div>

        {/* Address */}
        <div style={{ marginBottom: '20px' }}>
          <label style={lbl}>Address</label>
          <textarea name="address" value={formData.address} onChange={handleChange} rows="2"
            style={{ ...inp(), resize: 'vertical' }} />
        </div>

        {/* Emergency contact */}
        <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px', marginBottom: '24px' }}>
          <h4 style={{ color: '#2d3748', marginBottom: '16px', fontWeight: '600' }}>Emergency Contact</h4>
          <div style={row2}>
            <div>
              <label style={lbl}>Name *</label>
              <input name="emergency_contact_name" value={formData.emergency_contact_name} onChange={handleChange}
                placeholder="Contact name" style={inp(fieldErrors.emergency_contact_name)} />
              {fieldErrors.emergency_contact_name && <span style={{ color: '#e53e3e', fontSize: '0.8rem' }}>{fieldErrors.emergency_contact_name}</span>}
            </div>
            <div>
              <label style={lbl}>Phone *</label>
              <input type="tel" name="emergency_contact_phone" value={formData.emergency_contact_phone} onChange={handleChange}
                placeholder="+91XXXXXXXXXX" style={inp(fieldErrors.emergency_contact_phone)} />
              {fieldErrors.emergency_contact_phone && <span style={{ color: '#e53e3e', fontSize: '0.8rem' }}>{fieldErrors.emergency_contact_phone}</span>}
            </div>
          </div>
          <div style={{ maxWidth: '280px' }}>
            <label style={lbl}>Relationship</label>
            <select name="emergency_contact_relation" value={formData.emergency_contact_relation} onChange={handleChange} style={inp()}>
              <option value="">Select</option>
              {EMERGENCY_RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>

        <button type="submit" disabled={saving} style={btnPrimary}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
}

// ── Password tab ──────────────────────────────────────────────────────────────
function PasswordTab() {
  const [formData, setFormData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = e => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError(''); setSuccess('');
  };

  const handleSubmit = async e => {
    e.preventDefault(); setSaving(true); setError(''); setSuccess('');
    if (formData.new_password !== formData.confirm_password) {
      setError('New passwords do not match'); setSaving(false); return;
    }
    if (formData.new_password.length < 8 || !/[a-zA-Z]/.test(formData.new_password) || !/[0-9]/.test(formData.new_password)) {
      setError('Password must be at least 8 characters with at least one letter and one number');
      setSaving(false); return;
    }
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API_URL}/auth/change-password`,
        { current_password: formData.current_password, new_password: formData.new_password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Password changed successfully!');
      setFormData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: '460px' }}>
      <h2 style={{ color: '#2d3748', marginBottom: '24px', fontSize: '1.4rem', fontWeight: '700' }}>Change Password</h2>
      <Alert type="error"   msg={error} />
      <Alert type="success" msg={success} />
      <form onSubmit={handleSubmit}>
        {[
          { name: 'current_password', label: 'Current Password', placeholder: 'Enter current password' },
          { name: 'new_password',     label: 'New Password',     placeholder: 'Min 8 chars, one letter + one number' },
          { name: 'confirm_password', label: 'Confirm New Password', placeholder: 'Re-enter new password' },
        ].map(({ name, label, placeholder }) => (
          <div key={name} style={{ marginBottom: '16px' }}>
            <label style={lbl}>{label}</label>
            <input type="password" name={name} value={formData[name]} onChange={handleChange}
              placeholder={placeholder} required style={inp()} />
          </div>
        ))}
        <button type="submit" disabled={saving} style={{ ...btnPrimary, marginTop: '8px' }}>
          {saving ? 'Changing…' : 'Change Password'}
        </button>
      </form>
    </div>
  );
}

// ── Billing / Plan tab ────────────────────────────────────────────────────────
function BillingTab() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/auth/me/usage`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fmtDate = iso => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
  };

  const UsageBar = ({ label, used, softLimit, hardLimit }) => {
    const cap = hardLimit || softLimit;
    const pct = cap ? Math.min((used / cap) * 100, 100) : 0;
    const color = pct >= 100 ? '#c53030' : pct >= 80 ? '#d69e2e' : '#2b6cb0';
    return (
      <div style={{ marginBottom: '18px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px', alignItems:'flex-end' }}>
          <span style={{ fontWeight:'600', color:'#2d3748', fontSize:'0.9rem' }}>{label}</span>
          <span style={{ fontWeight:'700', color, fontSize:'0.85rem' }}>
            {used.toLocaleString()}
            {softLimit ? ` / ${softLimit.toLocaleString()}` : ''}
            {hardLimit && hardLimit !== softLimit ? ` (max ${hardLimit.toLocaleString()})` : ''}
          </span>
        </div>
        {(softLimit || hardLimit) && (
          <div style={{ height:'8px', background:'#e2e8f0', borderRadius:'4px', overflow:'hidden' }}>
            {/* soft cap marker */}
            {hardLimit && softLimit && (
              <div style={{ position:'relative' }}>
                <div style={{ height:'8px', width:`${pct}%`, background:color, borderRadius:'4px', transition:'width 0.4s' }} />
              </div>
            )}
            {!hardLimit && (
              <div style={{ height:'8px', width:`${pct}%`, background:color, borderRadius:'4px', transition:'width 0.4s' }} />
            )}
          </div>
        )}
        {hardLimit && softLimit && (
          <div style={{ position:'relative', height:'8px', background:'#e2e8f0', borderRadius:'4px', overflow:'hidden', marginTop:'-8px' }}>
            <div style={{ height:'8px', width:`${pct}%`, background:color, borderRadius:'4px', transition:'width 0.4s' }} />
            {/* soft cap line */}
            <div style={{ position:'absolute', top:0, bottom:0, left:`${(softLimit/hardLimit)*100}%`, width:'2px', background:'#f6ad55', zIndex:1 }} />
          </div>
        )}
        {pct >= 80 && softLimit && (
          <p style={{ margin:'4px 0 0', fontSize:'0.75rem', color: pct >= 100 ? '#c53030' : '#d69e2e', fontWeight:'600' }}>
            {pct >= 100 ? `Limit reached — new ${label.toLowerCase()} will be blocked` : `${Math.round(100-pct)}% remaining`}
          </p>
        )}
      </div>
    );
  };

  if (loading) return <div style={{ color:'#718096', paddingTop:'40px' }}>Loading…</div>;

  const tier = data?.plan_tier || 'free';
  const isPro = tier === 'pro';
  const usage = data?.usage || {};
  const limits = data?.limits || {};

  return (
    <div>
      <h2 style={{ color:'#2d3748', marginBottom:'6px', fontSize:'1.4rem', fontWeight:'700' }}>Plan & Usage</h2>
      <p style={{ color:'#718096', marginBottom:'32px', fontSize:'0.9rem' }}>Your current plan and data usage</p>

      {/* Plan card */}
      <div style={{
        border: isPro ? '2px solid #2b6cb0' : '2px solid #e2e8f0',
        borderRadius:'14px', padding:'24px 28px', marginBottom:'32px',
        background: isPro ? '#ebf4ff' : 'white',
        display:'flex', justifyContent:'space-between', alignItems:'flex-start',
      }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'6px' }}>
            <span style={{
              padding:'4px 14px', borderRadius:'20px', fontWeight:'800', fontSize:'0.85rem',
              background: isPro ? '#2b6cb0' : '#edf2f7',
              color: isPro ? 'white' : '#718096',
              letterSpacing:'0.05em', textTransform:'uppercase',
            }}>
              {isPro ? 'Pro' : 'Free'}
            </span>
            {isPro && data?.plan_expires_at && (
              <span style={{ fontSize:'0.8rem', color:'#4a5568', fontWeight:'600' }}>
                Valid until {fmtDate(data.plan_expires_at)}
              </span>
            )}
          </div>
          {isPro ? (
            <p style={{ color:'#2c5282', fontSize:'0.9rem', margin:'8px 0 0', fontWeight:'500' }}>
              You have unlimited access to all features.
            </p>
          ) : (
            <p style={{ color:'#4a5568', fontSize:'0.9rem', margin:'8px 0 0' }}>
              Free plan — limited storage and features. Upgrade to Pro for unlimited access.
            </p>
          )}
        </div>
        {!isPro && (
          <div style={{ textAlign:'right', flexShrink:0 }}>
            <div style={{ fontSize:'0.75rem', color:'#a0aec0', marginBottom:'8px' }}>Want more?</div>
            <a href="mailto:hireshrenik@gmail.com?subject=HRV Pro Upgrade" style={{
              display:'inline-block', padding:'9px 20px', background:'#2b6cb0', color:'white',
              borderRadius:'8px', fontWeight:'700', fontSize:'0.85rem', textDecoration:'none',
            }}>
              Contact to Upgrade →
            </a>
          </div>
        )}
      </div>

      {/* Usage */}
      <h3 style={{ color:'#2d3748', fontSize:'1.05rem', fontWeight:'700', marginBottom:'18px' }}>Data Usage</h3>
      <div style={{ background:'white', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'24px 28px', marginBottom:'20px' }}>
        <UsageBar
          label="Vital Signs"
          used={usage.vital_count || 0}
          softLimit={limits.vitals}
          hardLimit={limits.vitals}
        />
        <UsageBar
          label="Documents Uploaded"
          used={usage.doc_count || 0}
          softLimit={limits.docs}
          hardLimit={limits.docs}
        />
        <UsageBar
          label="Lab Parameters"
          used={usage.lab_count || 0}
          softLimit={limits.lab_soft}
          hardLimit={limits.lab_hard}
        />
        <div style={{ display:'flex', justifyContent:'space-between', paddingTop:'14px', borderTop:'1px solid #f0f4f8', marginTop:'6px' }}>
          <span style={{ fontWeight:'600', color:'#2d3748', fontSize:'0.9rem' }}>Patients / Family Members</span>
          <span style={{ fontWeight:'700', color:'#2b6cb0', fontSize:'0.85rem' }}>
            {usage.patient_count || 0}
            {!isPro ? ' (self only on free plan)' : ''}
          </span>
        </div>
      </div>

      {!isPro && (
        <div style={{ padding:'16px 20px', background:'#fffbeb', border:'1px solid #fbd38d', borderRadius:'10px', fontSize:'0.85rem', color:'#744210' }}>
          <strong>Free plan limits:</strong> 10 vitals · 5 documents · 100 lab parameters (150 max) · 1 patient profile (yourself) · AI features blocked when over limit.
        </div>
      )}
    </div>
  );
}

// ── APIs tab — LLM configuration ─────────────────────────────────────────────
// authUrl = clicking "Authorize" opens this in a new tab; user logs in there,
//           copies their API key/token, then pastes it back here.
const PROVIDERS = [
  {
    value: 'openai', label: 'OpenAI', defaultModel: 'gpt-4o-mini',
    authUrl: 'https://platform.openai.com/api-keys',
    authLabel: 'Sign in to OpenAI Platform',
    authSteps: ['Click "Authorize" to open OpenAI Platform', 'Sign in with your OpenAI account', 'Create a new API key and copy it', 'Paste it below'],
    note: 'GPT-4o, GPT-4o mini, o1, o3 and more',
  },
  {
    value: 'anthropic', label: 'Anthropic (Claude)', defaultModel: 'claude-3-5-haiku-20241022',
    authUrl: 'https://console.anthropic.com/settings/keys',
    authLabel: 'Sign in to Anthropic Console',
    authSteps: ['Click "Authorize" to open Anthropic Console', 'Sign in with your Anthropic account', 'Go to Settings → API Keys → Create Key', 'Copy the key and paste it below'],
    note: 'Claude 3.5 Haiku, Sonnet, Opus',
  },
  {
    value: 'gemini', label: 'Google Gemini', defaultModel: 'gemini-2.0-flash',
    authUrl: 'https://aistudio.google.com/app/apikey',
    authLabel: 'Sign in with Google (AI Studio)',
    authSteps: ['Click "Authorize" to open Google AI Studio', 'Sign in with your Google account', 'Click "Create API Key" and copy it', 'Paste it below'],
    note: 'Gemini 2.0 Flash, 1.5 Pro — free tier available',
  },
  {
    value: 'groq', label: 'Groq', defaultModel: 'llama-3.3-70b-versatile',
    authUrl: 'https://console.groq.com/keys',
    authLabel: 'Sign in to Groq Console',
    authSteps: ['Click "Authorize" to open Groq Console', 'Sign in or create a free account', 'Click "Create API Key" and copy it', 'Paste it below'],
    note: 'Ultra-fast inference — generous free tier',
  },
  {
    value: 'openrouter', label: 'OpenRouter', defaultModel: 'openai/gpt-4o-mini',
    authUrl: 'https://openrouter.ai/keys',
    authLabel: 'Sign in to OpenRouter',
    authSteps: ['Click "Authorize" to open OpenRouter', 'Sign in with Google or GitHub', 'Create an API key and copy it', 'Paste it below'],
    note: '100+ models from one key — GPT-4o, Claude, Gemini, Llama…',
  },
  {
    value: 'deepseek', label: 'DeepSeek', defaultModel: 'deepseek-chat',
    authUrl: 'https://platform.deepseek.com/api_keys',
    authLabel: 'Sign in to DeepSeek Platform',
    authSteps: ['Click "Authorize" to open DeepSeek Platform', 'Sign in or create an account', 'Create an API key and copy it', 'Paste it below'],
    note: 'DeepSeek-V3, DeepSeek-R1 — very affordable',
  },
  {
    value: 'moonshot', label: 'Kimi / Moonshot AI', defaultModel: 'moonshot-v1-8k',
    authUrl: 'https://platform.moonshot.cn/console/api-keys',
    authLabel: 'Sign in to Moonshot Platform',
    authSteps: ['Click "Authorize" to open Moonshot (Kimi) Platform', 'Sign in with your Moonshot account', 'Create an API key and copy it', 'Paste it below'],
    note: 'Kimi by Moonshot AI',
  },
  {
    value: 'qwen', label: 'Qwen (Alibaba)', defaultModel: 'qwen-turbo',
    authUrl: 'https://dashscope.console.aliyun.com/apiKey',
    authLabel: 'Sign in to Alibaba Cloud DashScope',
    authSteps: ['Click "Authorize" to open Alibaba DashScope', 'Sign in with your Alibaba account', 'Copy your API key', 'Paste it below'],
    note: 'Qwen2.5 series — free tier available',
  },
  {
    value: 'zhipuai', label: 'ZhipuAI (GLM)', defaultModel: 'glm-4-flash',
    authUrl: 'https://open.bigmodel.cn/usercenter/apikeys',
    authLabel: 'Sign in to ZhipuAI',
    authSteps: ['Click "Authorize" to open ZhipuAI', 'Sign in or create an account', 'Copy your API key', 'Paste it below'],
    note: 'GLM-4 Flash is free — Chinese provider',
  },
  {
    value: 'minimax', label: 'MiniMax', defaultModel: 'abab6.5s-chat',
    authUrl: 'https://www.minimaxi.com/user-center/basic-information/interface-key',
    authLabel: 'Sign in to MiniMax',
    authSteps: ['Click "Authorize" to open MiniMax', 'Sign in or create an account', 'Copy your API key', 'Paste it below'],
    note: 'MiniMax AI',
  },
  {
    value: 'together', label: 'Together AI', defaultModel: 'meta-llama/Llama-3-8b-chat-hf',
    authUrl: 'https://api.together.xyz/settings/api-keys',
    authLabel: 'Sign in to Together AI',
    authSteps: ['Click "Authorize" to open Together AI', 'Create a free account ($25 credits)', 'Copy your API key', 'Paste it below'],
    note: 'Open-source models — $25 free credits on signup',
  },
  {
    value: 'ollama-cloud', label: 'Ollama Cloud', defaultModel: 'gemma4:31b',
    authUrl: 'https://ollama.com',
    authLabel: 'Sign in to Ollama.com',
    authSteps: ['Click "Authorize" to open Ollama.com', 'Sign in or create an account', 'Go to Account → API Keys', 'Copy your API key and paste it below'],
    note: 'Run open-source models in the cloud',
  },
  {
    value: 'ollama-local', label: 'Ollama (Self-hosted)', defaultModel: 'llama3.2',
    noKey: true, needsBaseUrl: true,
    authSteps: ['Enter the URL of your Ollama server below', 'Default: http://localhost:11434', 'No API key required'],
    note: 'Your own Ollama server — no API key required',
  },
  {
    value: 'custom', label: 'Custom (OpenAI-compatible)', defaultModel: '',
    needsBaseUrl: true,
    authSteps: ['Enter your server\'s base URL below', 'Enter your API key if required', 'Must support /v1/chat/completions'],
    note: 'Any OpenAI-compatible server',
  },
];

function APIsTab() {
  const token   = useMemo(() => localStorage.getItem('token'), []);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [fetching,   setFetching]   = useState(false);
  const [configured, setConfigured] = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [testResult, setTestResult] = useState(null);
  const [modelList,  setModelList]  = useState([]);
  const [useDropdown, setUseDropdown] = useState(false);

  const [form, setForm] = useState({
    provider: 'openai', api_key: '', model: '', base_url: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`${API_URL}/auth/llm-config`, { headers });
        const c = res.data.config || {};
        if (c && c.provider) {
          setConfigured(!!c.configured);
          setForm(prev => ({
            ...prev,
            provider: c.provider || 'openai',
            model:    c.model    || '',
            base_url: c.base_url || '',
            api_key:  '',
          }));
        }
      } catch (_) {}
      finally { setLoading(false); }
    })();
  }, [headers]);

  const meta = PROVIDERS.find(p => p.value === form.provider) || PROVIDERS[0];

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'provider') {
      setModelList([]); setUseDropdown(false);
      setTestResult(null); setError(''); setSuccess('');
    } else {
      setError(''); setSuccess(''); setTestResult(null);
    }
  };

  const handleAuthorize = () => {
    if (meta.authUrl) window.open(meta.authUrl, '_blank', 'noopener,noreferrer');
  };

  const handleTest = async () => {
    if (!meta.noKey && !form.api_key.trim() && !configured) {
      setError('Paste your API key first, then test.'); return;
    }
    setTesting(true); setError(''); setSuccess(''); setTestResult(null);
    try {
      const res = await api.post(`${API_URL}/auth/llm-config/test`, form, { headers });
      setTestResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Request failed';
      setTestResult({ ok: false, error: msg });
    } finally { setTesting(false); }
  };

  const handleFetchModels = async () => {
    setFetching(true); setError('');
    try {
      const res = await api.post(`${API_URL}/auth/llm-config/models`, form, { headers });
      const models = res.data.models || [];
      setModelList(models);
      setUseDropdown(true);
      if (models.length) {
        const def = meta.defaultModel;
        const match = models.find(m => m === def) || (form.model && models.find(m => m === form.model)) || models[0];
        setForm(prev => ({ ...prev, model: match }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not fetch models from provider');
    } finally { setFetching(false); }
  };

  const handleSave = async e => {
    e.preventDefault();
    if (!meta.noKey && !form.api_key.trim() && !configured) {
      setError('API key is required.'); return;
    }
    if (meta.needsBaseUrl && !form.base_url.trim()) {
      setError('Base URL is required for this provider.'); return;
    }
    setSaving(true); setError(''); setSuccess(''); setTestResult(null);
    try {
      await api.put(`${API_URL}/auth/llm-config`, form, { headers });
      setConfigured(true);
      setSuccess('Configuration saved! AI features will now use your model.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save configuration.');
    } finally { setSaving(false); }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove LLM configuration? The app will fall back to built-in models.')) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put(`${API_URL}/auth/llm-config`,
        { provider: '', api_key: '', model: '', base_url: '' }, { headers });
      setConfigured(false);
      setForm({ provider: 'openai', api_key: '', model: '', base_url: '' });
      setModelList([]); setUseDropdown(false); setTestResult(null);
      setSuccess('Configuration removed. Using built-in models.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove configuration.');
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ color: '#718096' }}>Loading…</div>;

  const effectiveDefault = meta.defaultModel || '';

  return (
    <div style={{ maxWidth: '600px' }}>
      <h2 style={{ color: '#2d3748', marginBottom: '6px', fontSize: '1.4rem', fontWeight: '700' }}>AI / LLM Configuration</h2>
      <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '24px' }}>
        Connect your own AI provider — it will be used first for all AI features
        (document interpretation, health insights, Ask Doctor). Falls back to
        the built-in model chain if your key fails.
      </p>

      {configured && (
        <div style={{ background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#22543d', fontWeight: '600' }}>
            ✓ Connected: {PROVIDERS.find(p => p.value === form.provider)?.label || form.provider}
            {form.model ? ` — ${form.model}` : ''}
          </span>
          <button onClick={handleRemove} disabled={saving}
            style={{ background: 'none', border: '1px solid #c53030', color: '#c53030', borderRadius: '6px', padding: '4px 12px', fontSize: '0.82rem', cursor: 'pointer' }}>
            Disconnect
          </button>
        </div>
      )}

      <Alert type="error"   msg={error} />
      <Alert type="success" msg={success} />

      {testResult && (
        <div style={{
          padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
          background: testResult.ok ? (testResult.warning ? '#fffbeb' : '#f0fff4') : '#fff5f5',
          color:      testResult.ok ? (testResult.warning ? '#744210' : '#22543d') : '#c53030',
          border: `1px solid ${testResult.ok ? (testResult.warning ? '#fbd38d' : '#9ae6b4') : '#fed7d7'}`,
          fontSize: '0.9rem',
        }}>
          {testResult.ok ? (
            <>
              <div style={{ marginBottom: testResult.warning ? '6px' : 0 }}>
                ✓ {testResult.warning ? 'API key is valid' : 'Connection successful!'}&nbsp;
                <button onClick={handleFetchModels} disabled={fetching}
                  style={{ background: 'none', border: 'none', color: testResult.warning ? '#744210' : '#276749', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.9rem', padding: 0, fontWeight: '600' }}>
                  {fetching ? 'Loading models…' : '→ Pick a model'}
                </button>
              </div>
              {testResult.warning && (
                <div style={{ fontSize: '0.82rem', lineHeight: '1.5' }}>⚠ {testResult.warning}</div>
              )}
            </>
          ) : `✗ ${testResult.error}`}
        </div>
      )}

      <form onSubmit={handleSave}>

        {/* Step 1: Provider */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#a0aec0', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px' }}>Step 1 — Choose provider</div>
          <select name="provider" value={form.provider} onChange={handleChange}
            style={{ ...inp(), marginBottom: '6px' }}>
            {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          {meta.note && <div style={{ color: '#718096', fontSize: '0.82rem' }}>{meta.note}</div>}
        </div>

        {/* Step 2: Authorize / get key */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#a0aec0', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px' }}>
            Step 2 — {meta.noKey ? 'Configure server URL' : 'Authorize & get API key'}
          </div>

          {/* How-to steps */}
          {meta.authSteps && (
            <ol style={{ margin: '0 0 12px 0', paddingLeft: '20px', color: '#4a5568', fontSize: '0.85rem', lineHeight: '1.8' }}>
              {meta.authSteps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          )}

          {/* Authorize button for providers with a web auth URL */}
          {meta.authUrl && !meta.noKey && (
            <button type="button" onClick={handleAuthorize}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '10px 20px', background: '#2b6cb0', color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '0.9rem',
                fontWeight: '600', cursor: 'pointer', marginBottom: '14px',
              }}>
              🔑 {meta.authLabel || `Sign in to ${meta.label}`}
            </button>
          )}

          {/* Base URL for local/custom */}
          {meta.needsBaseUrl && (
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Server URL</label>
              <input
                name="base_url" value={form.base_url} onChange={handleChange}
                placeholder={form.provider === 'ollama-local' ? 'http://localhost:11434' : 'https://your-endpoint.com'}
                style={inp()}
              />
            </div>
          )}

          {/* API key paste field */}
          {!meta.noKey && (
            <div>
              <label style={lbl}>
                {meta.authUrl ? 'Paste your API key here' : 'API Key'}
              </label>
              <input
                type="password" name="api_key" value={form.api_key} onChange={handleChange}
                placeholder={configured ? '••••••••  (leave blank to keep existing key)' : 'Paste your API key'}
                style={inp()}
                autoComplete="new-password"
              />
              <small style={{ color: '#a0aec0', fontSize: '0.78rem' }}>Stored securely — only the first 8 chars shown once saved.</small>
            </div>
          )}
        </div>

        {/* Step 3: Test */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#a0aec0', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px' }}>Step 3 — Test connection</div>
          <button type="button" onClick={handleTest} disabled={testing || saving}
            style={{ ...btnPrimary, background: '#4a5568' }}>
            {testing ? 'Testing…' : 'Test Connection'}
          </button>
        </div>

        {/* Step 4: Pick model */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#a0aec0', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px' }}>
            Step 4 — Select model
            {!useDropdown && testResult?.ok && (
              <button type="button" onClick={handleFetchModels} disabled={fetching}
                style={{ marginLeft: '10px', background: 'none', border: 'none', color: '#2b6cb0', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontWeight: '700' }}>
                {fetching ? 'Loading…' : '↻ Fetch from provider'}
              </button>
            )}
          </div>

          {useDropdown && modelList.length > 0 ? (
            <>
              <select name="model" value={form.model} onChange={handleChange} style={inp()}>
                {modelList.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <small style={{ color: '#718096', fontSize: '0.78rem' }}>{modelList.length} models available</small>
            </>
          ) : (
            <>
              <input
                name="model" value={form.model} onChange={handleChange}
                placeholder={effectiveDefault ? `Default: ${effectiveDefault}` : 'Enter model name'}
                style={inp()}
              />
              <small style={{ color: '#a0aec0', fontSize: '0.78rem' }}>
                Leave blank to use default{effectiveDefault ? ` (${effectiveDefault})` : ''}.
                After testing, click "Fetch from provider" to see all available models.
              </small>
            </>
          )}
        </div>

        {/* Save */}
        <button type="submit" disabled={saving || testing}
          style={{ ...btnPrimary, padding: '11px 36px', fontSize: '1rem' }}>
          {saving ? 'Saving…' : configured ? 'Update Configuration' : 'Save Configuration'}
        </button>
      </form>
    </div>
  );
}

// ── Main Settings page ────────────────────────────────────────────────────────
function Settings({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'my-profile';

  useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);
  if (!user) return null;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <div className="dashboard">
      <Navbar user={user} onLogout={handleLogout} />

      <div style={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>

        {/* Left sidebar */}
        <aside style={{ width: '220px', flexShrink: 0, background: 'white', borderRight: '1px solid #e2e8f0', paddingTop: '20px' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              width: '100%', padding: '8px 16px', background: 'none',
              border: 'none', color: '#718096', fontSize: '0.85rem',
              cursor: 'pointer', textAlign: 'left', marginBottom: '8px',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#2d3748'}
            onMouseLeave={e => e.currentTarget.style.color = '#718096'}
          >
            ← Back
          </button>
          <div style={{ padding: '0 16px 12px', fontSize: '0.7rem', fontWeight: '700', color: '#a0aec0', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Settings
          </div>
          {NAV_ITEMS.map(item => {
            const active = activeTab === item.key;
            return (
              <button
                key={item.key}
                onClick={() => setSearchParams({ tab: item.key })}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 16px', background: active ? '#ebf4ff' : 'transparent',
                  border: 'none', borderLeft: `3px solid ${active ? '#2b6cb0' : 'transparent'}`,
                  color: active ? '#2b6cb0' : '#4a5568',
                  fontWeight: active ? '600' : '400', fontSize: '0.93rem',
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f7fafc'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <span>{item.icon}</span>
                {item.label}
              </button>
            );
          })}
        </aside>

        {/* Content area */}
        <main style={{ flex: 1, padding: '40px 48px', background: '#f7fafc', overflowY: 'auto' }}>
          {activeTab === 'my-profile' && <MyProfileTab user={user} />}
          {activeTab === 'password'   && <PasswordTab />}
          {activeTab === 'billing'    && <BillingTab />}
          {activeTab === 'apis'       && <APIsTab />}
        </main>

      </div>
    </div>
  );
}

export default Settings;
